<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.dealership', 2) },
      { label: $t('ui.common.statistic', 2) }
    ]"
  >
    <template #right>
      <div class="flex gap-2 items-stretch lg:items-center flex-col lg:flex-row flex-wrap md:gap-4 w-full lg:w-auto">
        <DealershipSelect
          v-model="dealershipsSelected"
          :chips="false"
          mode="multiple"
          placeholder="Select a Dealership"
        />
        <div class="flex gap-2">
          <DatePicker
            v-model="dateRange"
            mode="range"
            :presets="dateRangePresets"
          />
        </div>
        <Button
          label="Update"
          :is-loading="dataIsLoading"
          :disabled="!dealershipsSelected.length"
          @click="onDateRangeChange"
        />
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="computedData"
      export-filename="Dealerships Stats"
      removable-sort
      sort-field="dealershipName"
      :sort-order="-1"
      :rows="50"
      data-key="dealershipName"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-reports-dealerships-stats"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['dealershipName', 'manufacturer', 'groupName']"
      scrollable
      :loading="dataIsLoading"
    >
      <template #empty>
        <Empty
          v-if="!dataIsLoading && !dealershipsSelected.length"
          title="Select a dealership"
          icon="car-building"
        />
        <div v-else-if="!dataIsLoading">No records found</div>
      </template>
      <template #header>
        <div class="flex justify-between items-center">
          <span class="flex gap-4 items-center">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <div class="toggle-container">
              <ToggleSwitch
                v-model="uniqueCustomers"
                size="sm"
              />
              <div class="font-medium">Unique Customers</div>
            </div>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
          <Select
            v-model="columnsSelected"
            :options="columns"
            label-key="header"
            value-key="field"
            :chips="false"
            mode="multiple"
            on-select-return-as-object
            class="max-w-[200px]"
            placeholder="Select Columns"
            @change="onToggle"
          />
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <Column
        field="dealershipName"
        header="Dealership"
        :sortable="true"
        frozen
      >
        <template #body="{ data }">
          {{ data.dealershipName }}
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'targets.activeCustomers')"
        field="targets.activeCustomers"
        header="Active Cust"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'targets.callsAttempted')"
        field="targets.callsAttempted"
        header="Tar Attmpt"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'calls.attempted')"
        field="calls.attempted"
        header="Attempted"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <span>{{ data.calls.attempted }}</span>
          <Chip
            v-if="authStore.details.userType == 1"
            v-tippy="'% Attempted vs Tar.'"
            class="ml-2"
            size="sm"
            :severity="data.differences.callsAttempted >= 1 ? 'success' : 'danger'"
          >
            {{ numberFormatter(data.differences.callsAttempted * 100) }}%
          </Chip>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'calls.attempted')"
        field="differences.callsAttempted"
        header="% Attmpt"
        :sortable="true"
        hidden
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.callsAttempted * 100) }}%</span>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'targets.callsAnswered')"
        field="targets.callsAnswered"
        header="Tar Answ"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'calls.answered')"
        field="calls.answered"
        header="Answered"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <span>{{ data.calls.answered }}</span>
          <Chip
            v-if="authStore.details.userType == 1"
            v-tippy="'% Answered vs Attempted'"
            class="ml-2"
            size="sm"
            :severity="data.differences.callsAnswered >= 1 ? 'success' : 'danger'"
          >
            {{ numberFormatter(data.differences.callsAnswered * 100) }}%
          </Chip>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'calls.answered')"
        field="differences.callsAnswered"
        header="% Answ"
        hidden
        :sortable="true"
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.callsAnswered * 100) }}%</span>
        </template>
      </Column>
      <!-- <Column
        v-if="columnsSelected.find(col => col.field == 'targets.callsOpportunity')"
        field="targets.callsOpportunity"
        header="Tar Oppt"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'calls.opportunities')"
        field="calls.opportunities"
        header="Oppt"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <span>{{ data.calls.opportunities }}</span>
          <Chip
            v-if="authStore.details.userType == 1"
            v-tippy="'% Opportunity vs Answered'"
            class="ml-2"
            size="sm"
            :severity="data.differences.callsOpportunity > 1 ? 'success' : 'danger'"
          >
            {{ numberFormatter(data.differences.callsOpportunity * 100) }}%
          </Chip>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'calls.opportunities')"
        field="differences.callsOpportunity"
        header="% Oppt"
        :sortable="true"
        hidden
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.callsOpportunity * 100) }}%</span>
        </template>
      </Column> -->
      <Column
        v-if="columnsSelected.find(col => col.field == 'targets.appointmentsBooked')"
        field="targets.appointmentsBooked"
        header="Tar Booked"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.booked')"
        field="appointments.booked"
        header="Booked"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <span>{{ data.appointments.booked }}</span>
          <Chip
            v-if="authStore.details.userType == 1"
            v-tippy="'% Booked vs Opportunity'"
            class="ml-2"
            size="sm"
            :severity="data.differences.appointmentsBooked >= 1 ? 'success' : 'danger'"
          >
            {{ numberFormatter(data.differences.appointmentsBooked * 100) }}%
          </Chip>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.booked')"
        field="differences.appointmentsBooked"
        header="% Booked"
        :sortable="true"
        hidden
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.appointmentsBooked * 100) }}%</span>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'targets.appointmentsOccurring')"
        field="targets.appointmentsOccurring"
        header="Tar Occur"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.occurring')"
        field="appointments.occurring"
        header="Occurred"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <span>{{ data.appointments.occurring }}</span>
          <Chip
            v-if="authStore.details.userType == 1"
            v-tippy="'% Occurred vs Booked'"
            class="ml-2"
            size="sm"
            :severity="data.differences.appointmentsOccurring >= 1 ? 'success' : 'danger'"
          >
            {{ numberFormatter(data.differences.appointmentsOccurring * 100) }}%
          </Chip>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.occurring')"
        field="differences.appointmentsOccurring"
        header="% Occur"
        :sortable="true"
        hidden
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.appointmentsOccurring * 100) }}%</span>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.noShows')"
        field="appointments.noShows"
        header="No-Show"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'differences.appointmentsExpectedAttendances')"
        field="differences.appointmentsExpectedAttendances"
        header="Expected Attendance"
        :sortable="true"
      >
        <template #body="{ data }">
          <span>{{ data.differences.appointmentsExpectedAttendances }}</span>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'targets.appointmentsAttendances')"
        field="targets.appointmentsAttendances"
        header="Tar Attd"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.attended')"
        field="appointments.attended"
        header="Attd"
        :sortable="true"
        class="whitespace-nowrap"
      >
        <template #body="{ data }">
          <span>{{ data.appointments.attended }}</span>
          <Chip
            v-if="authStore.details.userType == 1"
            v-tippy="'% Attended vs Occurred'"
            class="ml-2"
            size="sm"
            :severity="data.differences.appointmentsAttended >= 1 ? 'success' : 'danger'"
          >
            {{ numberFormatter(data.differences.appointmentsAttended * 100) }}%
          </Chip>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.attended')"
        field="differences.appointmentsAttended"
        header="% Attd"
        :sortable="true"
        hidden
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.appointmentsAttended * 100) }}%</span>
        </template>
      </Column>
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.dealt')"
        field="appointments.dealt"
        header="Dealt"
        :sortable="true"
      />
      <Column
        v-if="columnsSelected.find(col => col.field == 'appointments.dealt')"
        field="differences.appointmentsDealt"
        header="% Dealt"
        :sortable="true"
        hidden
      >
        <template #body="{ data }">
          <span>{{ numberFormatter(data.differences.appointmentsDealt * 100) }}%</span>
        </template>
      </Column>

      <ColumnGroup type="footer">
        <Row>
          <Column
            :colspan="1"
            footer-style="text-align:left; padding: 14px 21px;"
          >
            <template #footer>
              {{ $t('ui.common.total', 1) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.activeCustomers')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.activeCustomers || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.callsAttempted')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.callsAttempted || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'calls.attempted')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.calls.attempted || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.callsAnswered')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.callsAnswered || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'calls.answered')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.calls.answered || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.callsOpportunity')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.callsOpportunity || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'calls.opportunities')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.calls.opportunities || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.appointmentsBooked')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.appointmentsBooked || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'appointments.booked')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.appointments.booked || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.appointmentsOccurring')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.appointmentsOccurring || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'appointments.occurring')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.appointments.occurring || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'targets.appointmentsAttendances')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.targets.appointmentsAttendances || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'appointments.attended')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.appointments.attended || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'appointments.noShows')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.appointments.noShows || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
          <Column
            v-if="columnsSelected.find(col => col.field == 'appointments.dealt')"
            footer-style="padding: 14px 21px;"
          >
            <template
              v-if="tableData.length"
              #footer
            >
              {{ tableData.map(d => d.appointments.dealt || 0).reduce((a, b) => a + b, 0) }}
            </template>
          </Column>
        </Row>
      </ColumnGroup>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import Card from '@/components/card/Card.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'
import DealershipSelect from '@/components/forms/DealershipSelect.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { datePresets } from '@/utilities/date-time'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import { FilterMatchMode } from '@primevue/core/api'
import dayjs from 'dayjs'
import Chip from '@/components/chip/Chip.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'

const authStore = useAuthStore()
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)
const dealershipsSelectedStorageKey = 'report-dealership-stats-dealerships-selected'

const dt = ref({ processedData: [] })
const dealershipsSelected = ref([])
const uniqueCustomers = ref(false)
const dealershipsSelectedLS = localStorage.getItem(dealershipsSelectedStorageKey)

if (dealershipsSelectedLS) {
  try {
    dealershipsSelected.value = JSON.parse(dealershipsSelectedLS)
  } catch {
    dealershipsSelected.value = []
  }
}

dealershipsSelected.value = dealershipsSelected.value.filter(d => dealershipsAllowed.value.map(da => da.id).includes(d))

if (dealershipsAllowed.value.length === 1 && dealershipsSelected.value.length === 0) {
  dealershipsSelected.value = [dealershipsAllowed.value[0].id]
}

localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(dealershipsSelected.value))

const columns = ref([
  { field: 'dealershipName', header: 'Dealership', allowedUserTypes: [1, 2], hideByDefault: false },
  {
    field: 'targets.activeCustomers',
    header: 'Active Customers',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  {
    field: 'targets.callsAttempted',
    header: 'Tar. Attempted',
    allowedUserTypes: [1],
    hideByDefault: true
  },
  { field: 'calls.attempted', header: 'Attempted', allowedUserTypes: [1, 2], hideByDefault: false },
  {
    field: 'targets.callsAnswered',
    header: 'Tar. Answered',
    allowedUserTypes: [1],
    hideByDefault: true
  },
  {
    field: 'calls.answered',
    header: 'Answered',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  // {
  //   field: 'targets.callsOpportunity',
  //   header: 'Tar. Opportunities',
  //   allowedUserTypes: [1],
  //   hideByDefault: true
  // },
  // {
  //   field: 'calls.opportunities',
  //   header: 'Opportunities',
  //   allowedUserTypes: [1, 2],
  //   hideByDefault: true
  // },
  {
    field: 'targets.appointmentsBooked',
    header: 'Tar. Booked',
    allowedUserTypes: [1],
    hideByDefault: true
  },
  {
    field: 'appointments.booked',
    header: 'Booked',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  {
    field: 'targets.appointmentsOccurring',
    header: 'Tar. Occurred',
    allowedUserTypes: [1],
    hideByDefault: true
  },
  {
    field: 'appointments.occurring',
    header: 'Occurred',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  {
    field: 'appointments.noShows',
    header: 'No-Show',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  {
    field: 'differences.appointmentsExpectedAttendances',
    header: 'Expected Attendance',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  {
    field: 'targets.appointmentsAttendances',
    header: 'Tar. Attended',
    allowedUserTypes: [1],
    hideByDefault: true
  },
  {
    field: 'appointments.attended',
    header: 'Attended',
    allowedUserTypes: [1, 2],
    hideByDefault: false
  },
  {
    field: 'appointments.dealt',
    header: 'Dealt',
    allowedUserTypes: [1, 2],
    hideByDefault: true
  }
])

columns.value = columns.value.filter(col => col.allowedUserTypes.includes(authStore.details.userType))

const columnsSelected = ref([])
if (localStorage.getItem('report-dealership-stats-columns')) {
  columnsSelected.value = JSON.parse(localStorage.getItem('report-dealership-stats-columns'))
} else {
  columnsSelected.value = columns.value.filter(col => col.hideByDefault == false)
}

const onToggle = val => {
  columnsSelected.value = columns.value.filter(col => {
    return JSON.stringify(val).includes(JSON.stringify(col))
  })

  localStorage.setItem('report-dealership-stats-columns', JSON.stringify(columnsSelected.value))
}

const dateRange = ref({
  start: dayjs().startOf('month').format('YYYY-MM-DD'),
  end: dayjs().format('YYYY-MM-DD')
})

const dateRangePresets = [
  datePresets.thisWeek,
  datePresets.thisMonth,
  {
    label: 'This Quarter',
    start: () => dayjs().startOf('quarter'),
    end: () => dayjs().endOf('quarter')
  }
]

const filters = ref({})
const setFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipName: { value: null, matchMode: FilterMatchMode.IN },
    groupName: { value: null, matchMode: FilterMatchMode.IN }
  }
}
setFilters()

const clearFilters = () => {
  // console.log('Clearing Filter')
  setFilters()
}

const dealershipStats = ref([])

const apiRequest = useApiRequest()
const dataIsLoading = ref(false)
function fetchData() {
  dataIsLoading.value = true

  apiRequest
    .send({
      endpoint: '/v1/reports/dealerships/statistics',
      params: {
        dealerships: dealershipsSelected.value.join(','),
        start: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
        end: dayjs(dateRange.value.end).format('YYYY-MM-DD'),
        uniqueCustomers: uniqueCustomers.value
      }
    })
    .then(response => {
      dealershipStats.value = response.data
      dataIsLoading.value = false
    })
}

function onDateRangeChange() {
  // Refetch reloops
  if (dealershipsSelected.value.length && dateRange.value.start && dateRange.value.end) {
    fetchData()
  }
}

// Watch for uniqueCustomers toggle changes and refetch data if filters are already set
watch(uniqueCustomers, () => {
  if (dealershipsSelected.value.length && dateRange.value.start && dateRange.value.end) {
    fetchData()
  }
})

watch(
  dealershipsSelected,
  value => {
    localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(value))
  },
  { deep: true }
)

const tableData = computed(() => {
  return dt.value.processedData
})

const exportCSV = () => {
  dt.value.exportCSV()
}

const actualVsTarget = (actual, target) => {
  return (target ? actual / target : 0).toFixed(2)
}

const computedData = computed(() => {
  const dealershipsData = []
  // Loop through each dealership
  dealershipStats.value.forEach(dealership => {
    const dealershipData = {
      dealershipName: dealership.dealershipName,
      groupName: findGroup(dealership.dealershipId),
      targets: dealership.targets,
      calls: dealership.calls,
      appointments: dealership.appointments,
      differences: {
        appointmentsExpectedAttendances: actualVsTarget(
          dealership.appointments.expectedAttendances,
          dealership.targets.appointmentsExpectedAttendances
        ),
        callsAttempted: actualVsTarget(dealership.calls.attempted, dealership.targets.callsAttempted),
        callsAnswered: actualVsTarget(dealership.calls.answered, dealership.targets.callsAnswered),
        callsOpportunity: actualVsTarget(dealership.calls.opportunities, dealership.targets.callsOpportunity),
        appointmentsBooked: actualVsTarget(dealership.appointments.booked, dealership.targets.appointmentsBooked),
        appointmentsOccurring: actualVsTarget(
          dealership.appointments.occurring,
          dealership.targets.appointmentsOccurring
        ),
        appointmentsExpectedAttendances: dealership.differences.appointmentsExpectedAttendances,
        appointmentsAttended: actualVsTarget(
          dealership.appointments.attended,
          dealership.targets.appointmentsAttendances
        ),
        appointmentsDealt: actualVsTarget(dealership.appointments.dealt, dealership.appointments.attended)
      }
    }
    dealershipsData.push(dealershipData)
  })
  return dealershipsData
})

function findGroup(id) {
  const groupId = authStore.accessAllowed.dealerships.find(d => d.id == id)?.groupId
  const groupObj = authStore.accessAllowed.groups.find(g => g.id == groupId)
  return groupObj.name
}
</script>

<style>
.toggle-container {
  border: 1px solid var(--border-secondary);
  border-radius: var(--rounded-md);
  padding: var(--s-2) var(--s-3) var(--s-2) var(--s-2);
  text-align: center;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-secondary);
}
</style>
